(function () {
  const normalizeValue = (value) => (value ?? "").toString().trim().toLowerCase();
  const tokenizeValue = (value) =>
    normalizeValue(value)
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  const matchesLoose = (optionValue, candidate) => {
    if (!optionValue || !candidate) return false;
    if (optionValue === candidate) return true;
    const optionTokens = tokenizeValue(optionValue);
    const candidateTokens = tokenizeValue(candidate);
    if (candidate.length <= 2) {
      return optionTokens.includes(candidate);
    }
    if (optionValue.length <= 2) {
      return candidateTokens.includes(optionValue);
    }
    return optionValue.includes(candidate) || candidate.includes(optionValue);
  };
  const toNumber = (value) => {
    if (value === null || value === void 0 || value === "") return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const CUSTOMIZATION_PARAMS = [
    "cover_message",
    "cover_small",
    "cover_square",
    "cover_add_date",
    "cover_date",
    "cover_gift",
    "cover_gift_number",
    "cover_gift_message"
  ];
  const DATE_MAX_LENGTH = 12;
  const DATE_SPACING_RESERVE_CHARS = 3;
  const COVER_INITIAL_MAX_LENGTH = 40;
  const OPTION_PARAM_PREFIX = "cover_option_";
  const normalizeYesNoParam = (value) => {
    const normalized = normalizeValue(value);
    if (["oui", "yes", "true", "1"].includes(normalized)) return "Oui";
    if (["non", "no", "false", "0"].includes(normalized)) return "Non";
    return null;
  };
  const toCssFontFamily = (value) => {
    const family = (value || "sans-serif").toString().trim();
    if (!family || family === "sans-serif") return "sans-serif";
    return `"${family.replace(/["\\]/g, "\\$&")}"`;
  };
  const resolveFontFamily = (value) => {
    const family = (value || "").toString().trim();
    if (/^https?:\/\//i.test(family)) return "Cover Custom Font";
    if (normalizeValue(family) === "bai jamjuree") return "Cover Bai Jamjuree";
    return family;
  };
  const getFilenameWithoutExtension = (url) => {
    const cleanUrl = (url || "").toString().split("?")[0].split("#")[0];
    const filename = decodeURIComponent(cleanUrl.split("/").pop() || "").trim();
    return filename.replace(/\.[a-z0-9]+$/i, "");
  };
  const isSvgUrl = (url) => /\.svg(?:[?#].*)?$/i.test((url || "").toString());
  const toCssUrl = (url) => `url("${(url || "").toString().replace(/["\\]/g, "\\$&")}")`;
  const toCssSize = (value) => {
    if (value === null || value === void 0 || value === "") return "";
    const size = value.toString().trim();
    if (!size) return "";
    const parsed = Number.parseFloat(size);
    return Number.isFinite(parsed) ? `${parsed}% auto` : "";
  };
  const clampPercent = (value) => {
    const parsed = toNumber(value);
    if (parsed === null) return null;
    return Math.min(100, Math.max(0, parsed));
  };
  const parseTextExclusionMask = (value) => {
    if (value === null || value === void 0) return null;
    if (typeof value === "object") {
      const left = clampPercent(value.left);
      const top = clampPercent(value.top ?? 0);
      const right = clampPercent(value.right);
      const bottom = clampPercent(value.bottom ?? 100);
      if (left === null || right === null || top === null || bottom === null) return null;
      if (right <= left || bottom <= top) return null;
      return { left, top, right, bottom };
    }
    const numbers = value
      .toString()
      .match(/-?\d+(?:\.\d+)?/g)
      ?.map((entry) => clampPercent(entry))
      .filter((entry) => entry !== null);
    if (!numbers || (numbers.length !== 2 && numbers.length < 4)) return null;
    const [firstX, firstY, secondX, rawSecondY] =
      numbers.length >= 4 ? numbers : [numbers[0], 0, numbers[1], 100];
    const secondY = numbers.length >= 5 && rawSecondY === firstY ? clampPercent(firstY + numbers[4]) : rawSecondY;
    const left = Math.min(firstX, secondX);
    const right = Math.max(firstX, secondX);
    const top = Math.min(firstY, secondY);
    const bottom = Math.max(firstY, secondY);
    if (right <= left || bottom <= top) return null;
    return { left, top, right, bottom };
  };
  const pointIsInsideTextExclusionMask = (mask, x, y) => {
    if (!mask) return false;
    return x >= mask.left && x <= mask.right && y >= mask.top && y <= mask.bottom;
  };
  const humanizePatternName = (value) =>
    (value || "")
      .toString()
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const HEART_CHARACTER = "\u2665";
  const HEART_INPUT_CHARACTER = "\u2764\ufe0f";
  const HEART_SVG =
    '<svg class="cover-customization__heart" viewBox="0 0 33.1 28.45" aria-label="Coeur" role="img"><path fill="currentColor" d="M.05,9.25c-.25,2.12.47,4.13,1.56,6.02,2.88,4.99,7.05,8.76,11.68,12.04,2.23,1.58,4.41,1.49,6.42-.09,3.1-2.44,6.28-4.78,8.81-7.85,2.65-3.23,4.84-6.63,4.56-11.1-.34-5.3-5.44-9.4-10.98-7.93-2.16.57-3.6,2.16-4.83,3.9-.59.84-1.06.82-1.56,0-.53-.85-1.28-1.5-2-2.15C11.78.37,9.51-.39,6.97.19,3.13,1.07-.43,4.6.05,9.25Z"/><path fill="currentColor" d="M.05,9.25C-.43,4.6,3.13,1.07,6.97.19c2.54-.58,4.81.18,6.73,1.91.72.65,1.47,1.29,2,2.15.5.81.97.83,1.56,0,1.23-1.75,2.67-3.33,4.83-3.9,5.55-1.47,10.65,2.63,10.98,7.93.29,4.47-1.9,7.87-4.56,11.1-2.53,3.07-5.72,5.42-8.81,7.85-2.01,1.58-4.19,1.68-6.42.09-4.63-3.28-8.8-7.05-11.68-12.04C.51,13.38-.2,11.37.05,9.25Z"/></svg>';
  const EMOJI_REGEX = /[\p{Extended_Pictographic}\u{1f1e6}-\u{1f1ff}\u{1f3fb}-\u{1f3ff}\u{20e3}\u{200d}\ufe0f]/gu;
  const normalizeMessageValue = (value) =>
    (value ?? "")
      .toString()
      .replace(/\u2764\ufe0f?/gi, HEART_CHARACTER)
      .replace(/[\u{1f493}-\u{1f49f}\u{1f5a4}\u{1fa75}-\u{1fa77}]/gu, HEART_CHARACTER)
      .replace(EMOJI_REGEX, (char) => (char === HEART_CHARACTER ? HEART_CHARACTER : ""));
  const normalizeLetterCaseValue = (value) => {
    const normalized = normalizeValue(value)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");
    if (["minuscule", "minuscules", "miniscule", "miniscules", "lowercase", "lower"].includes(normalized)) return "lowercase";
    if (["libre", "free", "none", "original"].includes(normalized)) return "free";
    return "uppercase";
  };
  const normalizeSecondLineMode = (value) => {
    const tokens = tokenizeValue(value);
    if (tokens.includes("180")) return "rotate";
    if (tokens.includes("normal")) return "mirror";
    if (tokens.includes("non") || tokens.includes("none") || tokens.includes("no")) return "plain";
    return "mirror";
  };
  const applyLetterCase = (value, letterCase) => {
    const normalizedValue = normalizeMessageValue(value);
    const normalizedLetterCase = normalizeLetterCaseValue(letterCase);
    if (normalizedLetterCase === "lowercase") return normalizedValue.toLocaleLowerCase("fr-FR");
    if (normalizedLetterCase === "free") return normalizedValue;
    return normalizedValue.toLocaleUpperCase("fr-FR");
  };
  const isBlankCharacter = (value) => /\s/.test(value);
  const escapeHtml = (value) => {
    if (value === null || value === void 0) return "";
    return value.toString().replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        case "'":
          return "&#39;";
        default:
          return char;
      }
    });
  };
  const renderMessageFragment = (value) =>
    Array.from(value || "")
      .map((char) => (char === HEART_CHARACTER ? HEART_SVG : escapeHtml(char)))
      .join("");
  const isConfigDebugEnabled = (configData) => ((configData || {}).config || {}).debug === true;

  class CoverCustomization extends HTMLElement {
    constructor() {
      super();
      this._abortController = new AbortController();
      this._resizeObserver = null;
      this._lightboxObserver = null;
      this._lightboxRaf = null;
      this._lightboxOverlay = null;
      this._lightboxPatternEl = null;
      this._lightboxFrame = null;
      this._lightboxMessageEl = null;
      this._lightboxDateEl = null;
      this._lightboxSlide = null;
      this._targetMedia = null;
      this._imageLoadAbortController = null;
      this._isVariantMediaPending = false;
      this._isApplyingUrlState = false;
      this._isApplyingVariantOptionsFromUrl = false;
      this._stateRefreshTimers = [];
      this._hasScrolled = false;
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.formId = this.getAttribute("data-form-id");
      this.errorMessage = this.getAttribute("data-error-message") || "Message trop long pour tenir sur une ligne";
      this.baiJamjureeFontUrl = this.getAttribute("data-bai-jamjuree-font-url");
      this.squareToken = this.getAttribute("data-square-token") || "IMAGE";
      this.configData = this.parseConfig();
      this.patterns = this.normalizePatterns((this.configData || {}).patterns || []);
      this.cacheElements();
      this.applyDisplayOptions();
      this.bindEvents();
      this.applyUrlStateFromCurrentLocation(true);
      requestAnimationFrame(() => this.applyUrlStateFromCurrentLocation(true));
      this.scheduleUrlStateRefreshes();
      this.observeLightbox();
      this.loadSimulatorFonts();
    }

    disconnectedCallback() {
      this._abortController.abort();
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._lightboxObserver) {
        this._lightboxObserver.disconnect();
        this._lightboxObserver = null;
      }
      this.stopLightboxTracking();
      this.stopWaitingForImageLoad();
      this._stateRefreshTimers.forEach((timer) => clearTimeout(timer));
      this._stateRefreshTimers = [];
    }

    parseConfig() {
      const script = this.querySelector("script[data-cover-config]");
      if (!script) return {};
      try {
        const config = JSON.parse(script.textContent || "{}");
        if (isConfigDebugEnabled(config)) {
          console.log("Cover customization config", config);
          console.log("Cover customization color debug", config?.config?.debugColorPattern);
        }
        return config;
      } catch (error) {
        console.warn("Cover customization: config JSON invalide", error);
        return {};
      }
    }

    normalizePatterns(patterns) {
      return patterns
        .map((pattern) => {
          const url = (pattern && pattern.url ? pattern.url : "").toString().trim();
          if (!url) return null;
          const name = getFilenameWithoutExtension(url);
          const alt = (pattern && pattern.alt ? pattern.alt : "").toString().trim();
          const label = alt || humanizePatternName(name);
          const contentType = (pattern && pattern.contentType ? pattern.contentType : "").toString().trim().toLowerCase();
          const mediaType = (pattern && pattern.mediaType ? pattern.mediaType : "").toString().trim().toLowerCase();
          const nameTokens = tokenizeValue(name);
          return {
            url,
            name,
            alt,
            label,
            trailingToken: nameTokens[nameTokens.length - 1] || "",
            isSvg: isSvgUrl(url) || contentType.includes("svg") || mediaType.includes("svg"),
            candidates: [name, alt, label, humanizePatternName(name)].filter(Boolean).map(normalizeValue)
          };
        })
        .filter(Boolean);
    }

    normalizeSizeValue(value) {
      return (value || "").toString().trim().toLowerCase();
    }

    getSelectedSizeValue() {
     const checkedInput = this.querySelector('input[type="radio"][name*="Taille"]:checked, input[type="radio"][name*="taille"]:checked');
      if (checkedInput) {
       return this.normalizeSizeValue(checkedInput.value);
     }

     const selectedOption = this.querySelector('select[name*="Taille"], select[name*="taille"]');
      if (selectedOption) {
       return this.normalizeSizeValue(selectedOption.value);
     }

     return "";
    }

    getCurrentSizeConfig() {
     const sizes = Array.isArray(this.configData?.sizes) ? this.configData.sizes : [];
     const selectedSize = this.getSelectedSizeValue();

     if (!selectedSize) return sizes[0] || null;

     return (
      sizes.find((size) => {
       const title = this.normalizeSizeValue(size?.title);
       const label = this.normalizeSizeValue(size?.label);
        return title === selectedSize || label === selectedSize;
     }) || sizes[0] || null
     );
    }

    getPatternsForCurrentSize() {
     const currentSize = this.getCurrentSizeConfig();
     const sizePatterns = Array.isArray(currentSize?.patterns) ? currentSize.patterns : [];
     const fallbackPatterns = Array.isArray(this.configData?.patterns) ? this.configData.patterns : [];

     return this.normalizePatterns(sizePatterns.length ? sizePatterns : fallbackPatterns);
    
  }
    loadSimulatorFonts() {
      const config = (this.configData || {}).config || {};
      const fontFamily = (config.fontFamily || "").toString().trim();
      const familyIsUrl = /^https?:\/\//i.test(fontFamily);
      const fontUrl = familyIsUrl ? fontFamily : config.fontUrl || this.baiJamjureeFontUrl;
      const loadedFamily = familyIsUrl ? "Cover Custom Font" : "Cover Bai Jamjuree";
      if (!fontUrl || !document.fonts || !window.FontFace) return;
      const fontWeight = (config.fontWeight || "600").toString();
      const fontKey = `${loadedFamily}-${fontWeight}-${fontUrl}`;
      CoverCustomization.loadedFonts = CoverCustomization.loadedFonts || new Set();
      if (CoverCustomization.loadedFonts.has(fontKey)) return;
      CoverCustomization.loadedFonts.add(fontKey);
      const font = new FontFace(loadedFamily, `url("${fontUrl}") format("truetype")`, {
        weight: fontWeight,
        style: "normal"
      });
      font
        .load()
        .then((loadedFont) => {
          document.fonts.add(loadedFont);
          this.syncTypography();
          this.renderMessage();
        })
        .catch((error) => {
          console.warn("Cover customization: chargement police Bai Jamjuree impossible", error);
        });
    }

    cacheElements() {
      this.messageInput = this.querySelector('[data-role="message"]');
      this.addHeartButton = this.querySelector('[data-role="add-heart"]');
      this.remainingCountEl = this.querySelector('[data-role="remaining-count"]');
      this.remainingWrapperEl = this.querySelector('[data-role="remaining-wrapper"]');
      this.errorEl = this.querySelector('[data-role="error"]');
      this.modifiedNoticeEl = this.querySelector('[data-role="modified-notice"]');
      this.squareInput = this.querySelector('[data-role="square-input"]');
      this.smallInput = this.querySelector('[data-role="small-input"]');
      this.dateInputHidden = this.querySelector('[data-role="date-input-hidden"]');
      this.giftInputHidden = this.querySelector('[data-role="gift-input-hidden"]');
      this.dateWrapper = this.querySelector('[data-role="date-wrapper"]');
      this.dateInput = this.querySelector('[data-role="date-input"]');
      this.giftWrapper = this.querySelector('[data-role="gift-wrapper"]');
      this.giftNumberInput = this.querySelector('[data-role="gift-number"]');
      this.giftMessageInput = this.querySelector('[data-role="gift-message"]');
      this.totalMessageInput = this.querySelector('[data-role="total-message"]');
      this.toggleSquare = this.querySelector('[data-toggle="square"]');
      this.toggleSmall = this.querySelector('[data-toggle="small"]');
      this.toggleDate = this.querySelector('[data-toggle="date"]');
      this.toggleGift = this.querySelector('[data-toggle="gift"]');
      this.optionSquare = this.querySelector('[data-option="square"]');
      this.optionSmall = this.querySelector('[data-option="small"]');
      this.optionDate = this.querySelector('[data-option="date"]');
      this.optionGift = this.querySelector('[data-option="gift"]');
      this.formElement = this.formId ? document.getElementById(this.formId) : null;
    }

    bindEvents() {
      if (this.messageInput) {
        this.messageInput.addEventListener(
          "input",
          () => {
            const messageValue = this.normalizeMessageForConfig(this.messageInput.value);
            if (this.messageInput.value !== messageValue) this.messageInput.value = messageValue;
            this.updateHeartControls();
            this.setModifiedNotice(false);
            const trimmedMessage = messageValue.trim();
            if (!this._isApplyingUrlState && !this._hasScrolled && trimmedMessage.length > 0) {
              this.refreshFromVariant();
              this.updateUrlState();
              this.scrollToImage();
              this._hasScrolled = true;
              return;
            }
            this.renderMessage();
            this.updateUrlState();
          },
          { signal: this._abortController.signal }
        );
        ["click", "keyup", "select"].forEach((eventName) => {
          this.messageInput.addEventListener(eventName, () => this.updateHeartControls(), { signal: this._abortController.signal });
        });
      }
      if (this.addHeartButton && this.messageInput) {
        this.addHeartButton.addEventListener(
          "click",
          () => {
            this.insertHeartInMessageInput();
          },
          { signal: this._abortController.signal }
        );
      }
      if (this.toggleSquare) {
        this.toggleSquare.addEventListener(
          "click",
          (event) => {
            const button = event.target.closest("button");
            if (!button) return;
            this.setToggleValue("square", button.getAttribute("data-value"));
            this.renderMessage();
            this.updateUrlState();
          },
          { signal: this._abortController.signal }
        );
      }
      if (this.toggleSmall) {
        this.toggleSmall.addEventListener(
          "click",
          (event) => {
            const button = event.target.closest("button");
            if (!button) return;
            this.setToggleValue("small", button.getAttribute("data-value"));
            this.refreshFromVariant();
            this.updateUrlState();
          },
          { signal: this._abortController.signal }
        );
      }
      if (this.toggleDate) {
        this.toggleDate.addEventListener(
          "click",
          (event) => {
            const button = event.target.closest("button");
            if (!button) return;
            this.setToggleValue("date", button.getAttribute("data-value"));
            this.renderMessage();
            this.updateUrlState();
          },
          { signal: this._abortController.signal }
        );
      }
      if (this.toggleGift) {
        this.toggleGift.addEventListener(
          "click",
          (event) => {
            const button = event.target.closest("button");
            if (!button) return;
            this.setToggleValue("gift", button.getAttribute("data-value"));
            this.updateUrlState();
          },
          { signal: this._abortController.signal }
        );
      }
      if (this.dateInput) {
        this.dateInput.addEventListener(
          "input",
          () => {
            const normalizedValue = normalizeMessageValue(this.dateInput.value);
            if (this.dateInput.value !== normalizedValue) this.dateInput.value = normalizedValue;
            this.renderMessage();
            this.updateUrlState();
          },
          { signal: this._abortController.signal }
        );
      }
      [this.giftNumberInput, this.giftMessageInput].filter(Boolean).forEach((input) => {
        input.addEventListener("input", () => this.updateUrlState(), { signal: this._abortController.signal });
      });
      document.addEventListener("variant:change", this.onVariantChange, { signal: this._abortController.signal });
      document.addEventListener("variant:changed", this.onVariantChange, { signal: this._abortController.signal });
      if (this.formElement) {
        this.formElement.addEventListener(
          "product:rerender",
          (event) => {
            const rerenderedVariant = this.getVariantFromFragment(event.detail?.htmlFragment);
            requestAnimationFrame(() => {
              this.applyStateFromUrl();
              this.refreshFromVariant(rerenderedVariant);
              this.scrollToImageIfMessageFilled();
            });
            setTimeout(() => {
              this.applyStateFromUrl();
              this.refreshFromVariant(rerenderedVariant);
              this.scrollToImageIfMessageFilled();
              this.updateUrlState();
            }, 150);
          },
          { signal: this._abortController.signal }
        );
      }
      document.addEventListener(
        "change",
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) return;
          if (!target.hasAttribute("data-option-position")) return;
          const root = this.closest(".product");
          if (root && !root.contains(target)) return;
          if (target.hasAttribute("data-product-url")) {
            this.updateUrlForProductChange(target.getAttribute("data-product-url"));
            return;
          }
          if (this._isApplyingVariantOptionsFromUrl) {
            requestAnimationFrame(() => {
              this.clearVariantMediaPending();
              this.applyStateFromUrl();
              this.refreshFromVariant();
              this.scrollToImageIfMessageFilled();
            });
            return;
          }
          this.markVariantMediaPending();
          requestAnimationFrame(() => {
            this.applyStateFromUrl();
            this.refreshFromVariant();
          });
          setTimeout(() => {
            this.applyStateFromUrl();
            this.refreshFromVariant();
            this.scrollToImageIfMessageFilled();
            this.updateUrlState();
          }, 250);
        },
        { signal: this._abortController.signal }
      );
      document.addEventListener(
        "click",
        (event) => {
          const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
          if (!link || !this.shouldPreserveStateForLink(link)) return;
          this.decorateStatePreservingLink(link);
        },
        { capture: true, signal: this._abortController.signal }
      );
    }

    onVariantChange(event) {
      if (!event.detail) return;
      if (event.detail.formId && event.detail.formId !== this.formId) return;
      this.clearVariantMediaPending();
      this.applyStateFromUrl();
      this.refreshFromVariant(event.detail.variant);
      requestAnimationFrame(() => {
        this.applyStateFromUrl();
        this.refreshFromVariant(event.detail.variant);
        this.scrollToImageIfMessageFilled();
      });
      setTimeout(() => {
        this.applyStateFromUrl();
        this.refreshFromVariant(event.detail.variant);
        this.scrollToImageIfMessageFilled();
        this.updateUrlState();
      }, 150);
    }

    applyUrlStateFromCurrentLocation(applyVariantOptions = false) {
      this.applyStateFromUrl();
      if (applyVariantOptions) this.applyVariantOptionsFromUrl();
      this.clearVariantMediaPending();
      this.refreshFromVariant();
      this.updateRemainingCount();
      this.renderMessage();
      this.updateUrlState();
      this.decorateStatePreservingLinks();
    }

    scheduleUrlStateRefreshes() {
      [120, 300, 700, 1400].forEach((delay) => {
        const timer = setTimeout(() => {
          if (!this.isConnected) return;
          this.applyUrlStateFromCurrentLocation(true);
        }, delay);
        this._stateRefreshTimers.push(timer);
      });
    }

    applyStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      this._isApplyingUrlState = true;
      const messageValue = params.get("cover_message");
      if (messageValue !== null && this.messageInput) {
        this.messageInput.value = this.normalizeMessageForConfig(messageValue);
        this.messageInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
      const squareValue = normalizeYesNoParam(params.get("cover_square"));
      if (squareValue) this.setToggleValue("square", squareValue);
      const smallValue = normalizeYesNoParam(params.get("cover_small"));
      if (smallValue) this.setToggleValue("small", smallValue);
      const dateValue = params.get("cover_date");
      const dateToggleValue = normalizeYesNoParam(params.get("cover_add_date")) || (dateValue !== null ? "Oui" : null);
      if (dateToggleValue) this.setToggleValue("date", dateToggleValue);
      if (dateValue !== null && this.dateInput) {
        this.dateInput.value = normalizeMessageValue(dateValue).trim().slice(0, DATE_MAX_LENGTH);
        this.dateInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
      const giftNumberValue = params.get("cover_gift_number");
      const giftMessageValue = params.get("cover_gift_message");
      const giftToggleValue = normalizeYesNoParam(params.get("cover_gift")) || (giftNumberValue !== null || giftMessageValue !== null ? "Oui" : null);
      if (giftToggleValue) this.setToggleValue("gift", giftToggleValue);
      if (giftNumberValue !== null && this.giftNumberInput) {
        this.giftNumberInput.value = giftNumberValue;
        this.giftNumberInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
      if (giftMessageValue !== null && this.giftMessageInput) {
        this.giftMessageInput.value = giftMessageValue;
        this.giftMessageInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
      this._isApplyingUrlState = false;
    }

    applyVariantOptionsFromUrl() {
      if (!this.formElement) return;
      const params = new URLSearchParams(window.location.search);
      const picker = this.getVariantPicker();
      if (!picker) return;
      let changedInput = null;
      Array.from(params.entries()).forEach(([key, value]) => {
        if (!key.startsWith(OPTION_PARAM_PREFIX) || !value) return;
        const position = key.slice(OPTION_PARAM_PREFIX.length);
        const input = this.findOptionInputByLabel(position, value);
        if (!input || input.disabled || input.hasAttribute("data-product-url")) return;
        if (!input.checked) input.checked = true;
        changedInput = input;
      });
      if (changedInput) {
        this._isApplyingVariantOptionsFromUrl = true;
        try {
          changedInput.dispatchEvent(new Event("change", { bubbles: true }));
        } finally {
          this._isApplyingVariantOptionsFromUrl = false;
        }
      }
    }

    updateUrlState() {
      if (this._isApplyingUrlState) return;
      if (!window.history || !window.location) return;
      const url = new URL(window.location.href);
      this.writeStateToSearchParams(url.searchParams);
      window.history.replaceState({ path: url.toString() }, "", url.toString());
      this.decorateStatePreservingLinks();
    }

    updateUrlForProductChange(productUrl) {
      if (!productUrl || !window.history || !window.location) return;
      try {
        const url = new URL(productUrl, window.location.origin);
        this.writeStateToSearchParams(url.searchParams);
        window.history.replaceState({ path: url.toString() }, "", url.toString());
      } catch (error) {
        this.updateUrlState();
      }
    }

    writeStateToSearchParams(searchParams) {
      this.clearStateSearchParams(searchParams);
      if (this.messageInput && this.messageInput.value.trim()) searchParams.set("cover_message", this.messageInput.value.trim());
      if (this.smallInput && this.smallInput.value === "Oui") searchParams.set("cover_small", "Oui");
      if (this.squareInput && this.squareInput.value === "Oui") searchParams.set("cover_square", "Oui");
      if (this.dateInputHidden && this.dateInputHidden.value === "Oui") {
        searchParams.set("cover_add_date", "Oui");
        if (this.dateInput && this.dateInput.value.trim()) searchParams.set("cover_date", this.dateInput.value.trim());
      }
      if (this.giftInputHidden && this.giftInputHidden.value === "Oui") {
        searchParams.set("cover_gift", "Oui");
        if (this.giftNumberInput && this.giftNumberInput.value.trim()) searchParams.set("cover_gift_number", this.giftNumberInput.value.trim());
        if (this.giftMessageInput && this.giftMessageInput.value.trim()) searchParams.set("cover_gift_message", this.giftMessageInput.value.trim());
      }
      this.getSelectedOptionInputs().forEach((input) => {
        const position = input.getAttribute("data-option-position");
        const label = this.getLabelFromOptionInput(input);
        if (position && label) searchParams.set(`${OPTION_PARAM_PREFIX}${position}`, label);
      });
    }

    clearStateSearchParams(searchParams) {
      CUSTOMIZATION_PARAMS.forEach((param) => searchParams.delete(param));
      Array.from(searchParams.keys()).forEach((key) => {
        if (key.startsWith(OPTION_PARAM_PREFIX)) searchParams.delete(key);
      });
    }

    getSelectedOptionInputs() {
      if (!this.formElement) return [];
      return Array.from(this.formElement.elements)
        .filter((item) => item.matches && item.matches("input[data-option-position]:checked"))
        .sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position")));
    }

    findOptionInputByLabel(position, label) {
      if (!this.formElement) return null;
      const normalizedLabel = normalizeValue(label);
      return Array.from(this.formElement.elements).find((item) => {
        if (!item.matches || !item.matches(`input[data-option-position="${position}"]`)) return false;
        const itemLabel = normalizeValue(this.getLabelFromOptionInput(item));
        return itemLabel && matchesLoose(itemLabel, normalizedLabel);
      });
    }

    getVariantPicker() {
      const root = this.closest(".product") || document;
      return this.formId ? root.querySelector(`variant-picker[form-id="${this.formId}"]`) : null;
    }

    shouldPreserveStateForLink(link) {
      const root = this.closest(".product");
      if (!root || !root.contains(link)) return false;
      if (!link.closest(".variant-picker")) return false;
      try {
        const url = new URL(link.href, window.location.href);
        return url.origin === window.location.origin;
      } catch (error) {
        return false;
      }
    }

    decorateStatePreservingLinks() {
      const root = this.closest(".product");
      if (!root) return;
      root.querySelectorAll(".variant-picker a[href]").forEach((link) => this.decorateStatePreservingLink(link));
    }

    decorateStatePreservingLink(link) {
      try {
        const url = new URL(link.getAttribute("href"), window.location.href);
        if (url.origin !== window.location.origin) return;
        this.writeStateToSearchParams(url.searchParams);
        link.setAttribute("href", url.pathname + url.search + url.hash);
      } catch (error) {
        // Ignore malformed theme editor links.
      }
    }

    applyDisplayOptions() {
      const config = (this.configData || {}).config || {};
      const displaySmall = config.displaySmallOption !== false;
      const displaySquare = false;
      const displayDate = config.displayDateOption !== false;
      const displayGift = config.displayGiftOption !== false;
      if (this.optionSmall) this.optionSmall.hidden = !displaySmall;
      if (this.optionSquare) this.optionSquare.hidden = !displaySquare;
      if (this.optionDate) this.optionDate.hidden = !displayDate;
      if (this.optionGift) this.optionGift.hidden = !displayGift;
      if (!displaySmall && this.smallInput) this.smallInput.value = "Non";
      if (!displaySquare && this.squareInput) this.squareInput.value = "Non";
      if (!displayDate && this.dateInputHidden) this.dateInputHidden.value = "Non";
      if (!displayGift && this.giftInputHidden) this.giftInputHidden.value = "Non";
      if (!displayDate && this.dateWrapper) this.dateWrapper.hidden = true;
      if (!displayGift && this.giftWrapper) this.giftWrapper.hidden = true;
      if (config.isTriangle) {
        this.classList.add("cover-customization--triangle");
      } else {
        this.classList.remove("cover-customization--triangle");
      }
    }

    applyMessageConstraints() {
      if (!this.messageInput) return;
      const config = this.getEffectiveConfig();
      const minLength = config.messageMinLength;
      const maxLength = config.messageMaxLength;
      if (minLength) {
        this.messageInput.setAttribute("minlength", `${minLength}`);
      } else {
        this.messageInput.removeAttribute("minlength");
      }
      if (maxLength) {
        this.messageInput.setAttribute("maxlength", `${maxLength}`);
      } else {
        this.messageInput.removeAttribute("maxlength");
      }
      this.updateRemainingCount();
    }

    insertHeartInMessageInput() {
      if (!this.messageInput) return;
      if (!this.canInsertHeartInMessageInput()) {
        this.updateHeartControls();
        this.messageInput.focus();
        return;
      }
      const maxLength = this.getEffectiveConfig().messageMaxLength || toNumber(this.messageInput.maxLength);
      const start = this.messageInput.selectionStart ?? this.messageInput.value.length;
      const end = this.messageInput.selectionEnd ?? this.messageInput.value.length;
      const nextValue = this.normalizeMessageForConfig(
        `${this.messageInput.value.slice(0, start)}${HEART_INPUT_CHARACTER}${this.messageInput.value.slice(end)}`
      );
      if (maxLength && nextValue.length > maxLength) {
        this.setError(true);
        this.messageInput.focus();
        return;
      }
      this.messageInput.value = nextValue;
      const nextCursor = start + HEART_CHARACTER.length;
      const safeCursor = Math.min(nextCursor, this.messageInput.value.length);
      this.messageInput.setSelectionRange(safeCursor, safeCursor);
      this.messageInput.dispatchEvent(new Event("input", { bubbles: true }));
      this.updateHeartControls();
      this.messageInput.focus();
    }

    updateRemainingCount() {
      if (!this.messageInput || !this.remainingCountEl) return;
      const maxLength = this.getEffectiveConfig().messageMaxLength || toNumber(this.messageInput.maxLength);
      if (!maxLength || maxLength <= 0) {
        if (this.remainingWrapperEl) this.remainingWrapperEl.hidden = true;
        return;
      }
      const currentLength = this.messageInput.value.length;
      const remaining = Math.max(0, maxLength - currentLength);
      this.remainingCountEl.textContent = `${remaining}`;
      if (this.remainingWrapperEl) this.remainingWrapperEl.hidden = false;
    }

    setToggleValue(name, value) {
      if (!value) return;
      const normalize = value === "Oui" ? "Oui" : "Non";
      let input = null;
      let toggle = null;
      if (name === "square") {
        input = this.squareInput;
        toggle = this.toggleSquare;
      } else if (name === "small") {
        input = this.smallInput;
        toggle = this.toggleSmall;
      } else if (name === "date") {
        input = this.dateInputHidden;
        toggle = this.toggleDate;
        if (this.dateWrapper) this.dateWrapper.hidden = normalize !== "Oui";
        if (this.dateInput) this.dateInput.required = normalize === "Oui";
        if (normalize !== "Oui" && this.dateInput) this.dateInput.value = "";
      } else if (name === "gift") {
        input = this.giftInputHidden;
        toggle = this.toggleGift;
        if (this.giftWrapper) this.giftWrapper.hidden = normalize !== "Oui";
        if (this.giftNumberInput) this.giftNumberInput.required = normalize === "Oui";
        if (normalize !== "Oui") {
          if (this.giftNumberInput) this.giftNumberInput.value = "";
          if (this.giftMessageInput) this.giftMessageInput.value = "";
        }
      }
      if (input) input.value = normalize;
      if (toggle) {
        toggle.querySelectorAll("button").forEach((button) => {
          const pressed = button.getAttribute("data-value") === normalize;
          button.setAttribute("aria-pressed", pressed ? "true" : "false");
        });
      }
    }

    refreshFromVariant(variant) {
      let resolvedVariant = variant || null;
      if (!resolvedVariant && this.formId) {
        const root = this.closest(".product") || document;
        const picker = root.querySelector(`variant-picker[form-id="${this.formId}"]`);
        if (picker && picker.selectedVariant) resolvedVariant = picker.selectedVariant;
        if (!resolvedVariant && picker) {
          const variantScript = picker.querySelector("script[data-variant]");
          if (variantScript) {
            try {
              const parsed = JSON.parse(variantScript.textContent || "{}");
              if (parsed && parsed.id) resolvedVariant = parsed;
            } catch (error) {
              console.warn("Cover customization: variant JSON invalide", error);
            }
          }
        }
      }
      if (!resolvedVariant) resolvedVariant = this.variant || null;
      this.variant = resolvedVariant;
      this.sizeConfig = this.resolveSizeConfig(this.variant);
      this.patterns = this.normalizePatterns(
        this.sizeConfig?.patterns?.length
        ? this.sizeConfig.patterns
        : (this.configData?.patterns || [])
      );
      this.updateMetaInfo();
      this.updateSizeDimensions();
      this.applyMessageConstraints();
      this.truncateMessageForCurrentSize();
      this.attachOverlay();
      this.applyFrame();
      this.syncTypography();
      this.applyFontColor();
      this.renderPattern();
      this.updateHeartControls();
      this.renderMessage();
      this.updateLightboxOverlay();
    }

    getVariantFromFragment(fragment) {
      if (!fragment || !this.formId) return null;
      const picker = fragment.querySelector(`variant-picker[form-id="${this.formId}"]`);
      const variantScript = picker ? picker.querySelector("script[data-variant]") : null;
      if (!variantScript) return null;
      try {
        const parsed = JSON.parse(variantScript.textContent || "{}");
        return parsed && parsed.id ? parsed : null;
      } catch (error) {
        console.warn("Cover customization: variant JSON invalide", error);
        return null;
      }
    }

    resolveSizeConfig(variant) {
     const sizes = (this.configData || {}).sizes || [];
     if (!sizes.length) return null;

     const sizeOptionValue = this.getSizeOptionValue(variant);
     const sizeOptionLabel = this.getSizeOptionLabel();

     for (const value of [sizeOptionValue, sizeOptionLabel]) {
      if (!value) continue;

      const normalizedValue = normalizeValue(value);
      const match = sizes.find(
      (size) => normalizeValue(size.label) === normalizedValue
      );

      if (match) return match;
      }

      const altMatch = this.resolveSizeConfigFromMedia(sizes);
      return altMatch || sizes[0];
    }

    getSizeOptionValue(variant) {
      const sizeFieldset = this.getSizeFieldset();
      if (!sizeFieldset) return null;
      const sampleInput = sizeFieldset.querySelector("[data-option-position]");
      const position = sampleInput ? Number(sampleInput.getAttribute("data-option-position")) : null;
      if (!position) return null;
      if (variant && variant[`option${position}`]) return variant[`option${position}`];
      const checkedInput = sizeFieldset.querySelector("input[data-option-position]:checked");
      if (!checkedInput) return null;
      return this.getLabelFromOptionInput(checkedInput);
    }

    getSizeOptionLabel() {
      const sizeFieldset = this.getSizeFieldset();
      if (!sizeFieldset) return null;
      const checkedInput = sizeFieldset.querySelector("input[data-option-position]:checked");
      if (!checkedInput) return null;
      return this.getLabelFromOptionInput(checkedInput);
    }

    getSizeFieldset() {
      const root = this.closest(".product") || document;
      const picker = this.formId ? root.querySelector(`variant-picker[form-id="${this.formId}"]`) : null;
      if (!picker) return null;
      const fieldsets = Array.from(picker.querySelectorAll(".variant-picker__option"));
      const sizeFieldset = fieldsets.find((fieldset) => {
        const legend = fieldset.querySelector("legend");
        if (!legend) return false;
        const text = legend.textContent.trim().toLowerCase();
        return text.includes("taille") || text.includes("size");
      });
      return sizeFieldset || null;
    }

    getLabelFromOptionInput(input) {
      if (!input || !input.id) return null;
      const label = input.closest("fieldset")?.querySelector(`label[for="${input.id}"]`);
      if (!label) return null;
      const text = label.textContent.replace(/\s+/g, " ").trim();
      return text || null;
    }

    resolveSizeConfigFromMedia(sizes) {
      const root = this.closest(".product") || document;
      const medias = Array.from(root.querySelectorAll(".product-gallery__media"));
      if (!medias.length) return null;
      const visibleMedias = medias.filter((media) => !media.hasAttribute("hidden"));
      const list = visibleMedias.length ? visibleMedias : medias;
      const altValues = list
        .map((media) => media.querySelector("img[alt]"))
        .filter(Boolean)
        .map((img) => normalizeValue(img.getAttribute("alt")));
      if (!altValues.length) return null;
      const matching = sizes.filter((size) => {
        const altCandidate = size.imageAlt ? normalizeValue(size.imageAlt) : "";
        if (!altCandidate) return false;
        return altValues.some((altValue) => matchesLoose(altValue, altCandidate));
      });
      if (matching.length === 1) return matching[0];
      return null;
    }

    applyFrame() {
      if (!this.frameEl || !this.sizeConfig) return;
      this.applyFrameToElement(this.frameEl);
    }

    applyFrameToElement(frameEl) {
      if (!frameEl || !this.sizeConfig) return;
      const frameTop = toNumber(this.sizeConfig.frameTop) || 0;
      const frameLeft = toNumber(this.sizeConfig.frameLeft) || 0;
      const frameRight = toNumber(this.sizeConfig.frameRight) || 0;
      const frameBottom = toNumber(this.sizeConfig.frameBottom) || 0;
      const isTriangle = ((this.configData || {}).config || {}).isTriangle === true;
      frameEl.style.top = `${frameTop}%`;
      frameEl.style.left = `${frameLeft}%`;
      frameEl.style.right = `${frameRight}%`;
      frameEl.style.bottom = `${frameBottom}%`;
      frameEl.classList.toggle("cover-customization__frame--triangle", isTriangle);
      frameEl.classList.toggle("cover-customization__frame--cover-initial", this.isCoverInitialLayout());
      this.clearTextExclusionMask(frameEl);
    }

    clearTextExclusionMask(frameEl) {
      if (!frameEl) return;
      frameEl.style.removeProperty("mask");
      frameEl.style.removeProperty("mask-image");
      frameEl.style.removeProperty("mask-size");
      frameEl.style.removeProperty("mask-repeat");
      frameEl.style.removeProperty("mask-position");
      frameEl.style.removeProperty("-webkit-mask");
      frameEl.style.removeProperty("-webkit-mask-image");
      frameEl.style.removeProperty("-webkit-mask-size");
      frameEl.style.removeProperty("-webkit-mask-repeat");
      frameEl.style.removeProperty("-webkit-mask-position");
    }

    updateMetaInfo() {
      if (!this.isDebugEnabled()) {
        this.metaInfoText = "";
        if (this.metaEl) {
          this.metaEl.textContent = "";
          this.metaEl.hidden = true;
        }
        return;
      }

      const configExists = this.configData && this.configData.config;
      if (!configExists) {
        const content = "metaobject unfound";
        this.metaInfoText = content;
        if (this.metaEl) {
          this.metaEl.textContent = content;
          this.metaEl.hidden = false;
        }
        return;
      }
      const configTitle = (this.configData.config.title || "").toString();
      const sizeTitle = this.sizeConfig && this.sizeConfig.title ? this.sizeConfig.title : "";
      const sizeLabel = this.sizeConfig && this.sizeConfig.label ? this.sizeConfig.label : "";
      const parts = [];
      if (configTitle) parts.push(configTitle);
      if (sizeTitle) {
        parts.push(sizeTitle);
      } else if (sizeLabel) {
        parts.push(sizeLabel);
      }
      const content = parts.join(" • ");
      this.metaInfoText = content;
      if (!this.metaEl) return;
      if (!content) {
        this.metaEl.textContent = "";
        this.metaEl.hidden = true;
        return;
      }
      this.metaEl.textContent = content;
      this.metaEl.hidden = false;
    }

    updateSizeDimensions() {
      const sizeFieldset = this.getSizeFieldset();
      if (!sizeFieldset) return;
      if (!this.sizeDimensionsEl || !sizeFieldset.contains(this.sizeDimensionsEl)) {
        this.sizeDimensionsEl = sizeFieldset.querySelector('[data-role="size-dimensions"]') || document.createElement("p");
        this.sizeDimensionsEl.className = "variant-picker__size-dimensions";
        this.sizeDimensionsEl.setAttribute("data-role", "size-dimensions");
        sizeFieldset.append(this.sizeDimensionsEl);
      }
      const dimensions = (this.sizeConfig && this.sizeConfig.dimensions ? this.sizeConfig.dimensions : "").toString().trim();
      this.sizeDimensionsEl.textContent = dimensions;
      this.sizeDimensionsEl.hidden = !dimensions;
    }

    getEffectiveConfig() {
      const base = (this.configData || {}).config || {};
      const size = this.sizeConfig || {};
      const isCoverInitial = this.isCoverInitialLayout();
      const isSmall = this.smallInput && this.smallInput.value === "Oui";
      const baseMaxChars = toNumber(size.maxChars);
      const baseMaxLines = toNumber(size.maxLines);
      const baseLineHeightPercent = toNumber(base.lineHeightPercent);
      const repeatOccurrences = base.repeatOccurrences !== false;
      const maxChars = isCoverInitial
        ? COVER_INITIAL_MAX_LENGTH
        : isSmall && toNumber(size.smallMaxChars)
          ? toNumber(size.smallMaxChars)
          : baseMaxChars;
      const maxLines = isCoverInitial
        ? 4
        : isSmall && toNumber(size.smallMaxLines)
          ? toNumber(size.smallMaxLines)
          : baseMaxLines;
      const lineHeightPercent = isSmall && toNumber(base.smallLineHeightPercent) ? toNumber(base.smallLineHeightPercent) : baseLineHeightPercent;
      const messageMinLength = toNumber(size.messageMinLength);
      const configuredMessageMaxLength = toNumber(size.messageMaxLength);
      const repeatedMessageMaxLength = maxChars ? Math.max(1, Math.floor((maxChars + 1) / 3) - 1) : null;
      const layoutMessageMaxLength = repeatOccurrences ? repeatedMessageMaxLength : maxChars;
      const messageMaxLength =
        configuredMessageMaxLength && layoutMessageMaxLength
          ? Math.min(configuredMessageMaxLength, layoutMessageMaxLength)
          : configuredMessageMaxLength || layoutMessageMaxLength;
      return {
        isCoverInitial,
        isSmall,
        repeatOccurrences,
        infinite: base.infinite === true,
        maxChars,
        maxLines,
        baseMaxLines: baseMaxLines || maxLines,
        baseLineHeightPercent: baseLineHeightPercent || lineHeightPercent || 100,
        lineHeightPercent: lineHeightPercent || 100,
        fontFamily: base.fontFamily,
        fontWeight: base.fontWeight,
        letterSpacing: base.letterSpacing,
        letterCase: normalizeLetterCaseValue(base.letterCase),
        secondLineMode: normalizeSecondLineMode(base.secondLine),
        messageMinLength,
        messageMaxLength
      };
    }

    isCoverInitialLayout() {
      const config = (this.configData || {}).config || {};
      return config.initiale === true;
    }

    isDebugEnabled() {
      return isConfigDebugEnabled(this.configData);
    }

    normalizeMessageForConfig(value) {
      return this.sanitizeHeartRules(applyLetterCase(value, this.getEffectiveConfig().letterCase));
    }

    heartsAreDisabledByPattern() {
      const value = normalizeValue(this.getPatternOptionValue(this.variant));
      if (!value) return false;
      const tokens = tokenizeValue(value);
      return !tokens.some((token) => ["non", "none", "no", "aucun", "sans"].includes(token));
    }

    sanitizeHeartRules(value) {
      let sanitizedValue = normalizeMessageValue(value);
      if (this.heartsAreDisabledByPattern()) return sanitizedValue.split(HEART_CHARACTER).join("");

      const cleaned = [];
      Array.from(sanitizedValue).forEach((char) => {
        if (char !== HEART_CHARACTER) {
          cleaned.push(char);
          return;
        }
        const lastMeaningfulChar = [...cleaned].reverse().find((entry) => !isBlankCharacter(entry));
        if (lastMeaningfulChar === HEART_CHARACTER) return;
        cleaned.push(char);
      });

      const trimmedChars = cleaned.join("");
      const chars = Array.from(trimmedChars);
      const firstMeaningfulIndex = chars.findIndex((char) => !isBlankCharacter(char));
      let lastMeaningfulIndex = -1;
      for (let index = chars.length - 1; index >= 0; index -= 1) {
        if (!isBlankCharacter(chars[index])) {
          lastMeaningfulIndex = index;
          break;
        }
      }

      if (
        firstMeaningfulIndex >= 0 &&
        lastMeaningfulIndex > firstMeaningfulIndex &&
        chars[firstMeaningfulIndex] === HEART_CHARACTER &&
        chars[lastMeaningfulIndex] === HEART_CHARACTER
      ) {
        chars.splice(lastMeaningfulIndex, 1);
      }

      return chars.join("");
    }

    canInsertHeartInMessageInput() {
      if (!this.messageInput || this.heartsAreDisabledByPattern()) return false;
      const start = this.messageInput.selectionStart ?? this.messageInput.value.length;
      const end = this.messageInput.selectionEnd ?? this.messageInput.value.length;
      const currentValue = this.normalizeMessageForConfig(this.messageInput.value);
      const nextValue = this.normalizeMessageForConfig(`${this.messageInput.value.slice(0, start)}${HEART_INPUT_CHARACTER}${this.messageInput.value.slice(end)}`);
      return nextValue !== currentValue;
    }

    updateHeartControls() {
      if (!this.addHeartButton) return;
      const disabledByPattern = this.heartsAreDisabledByPattern();
      this.addHeartButton.hidden = disabledByPattern;
      this.addHeartButton.disabled = disabledByPattern || !this.canInsertHeartInMessageInput();
    }

    syncTypography() {
      this.syncTypographyFor(this.frameEl, this.messageEl, this.dateEl);
    }

    syncTypographyFor(frameEl, messageEl, dateEl) {
      if (!frameEl || !messageEl) return;
      const config = this.getEffectiveConfig();
      if (!config.maxLines) return;
      const rect = frameEl.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      if (config.isCoverInitial) {
        const fontSize = Math.max(8, Math.min(rect.width, rect.height) * 0.032);
        frameEl.style.setProperty("--cover-border-font-size", `${fontSize}px`);
        messageEl.style.fontSize = `${fontSize}px`;
        messageEl.style.lineHeight = "1";
        if (config.fontFamily) {
          messageEl.style.fontFamily = resolveFontFamily(config.fontFamily);
        } else {
          messageEl.style.removeProperty("font-family");
        }
        if (config.fontWeight) {
          messageEl.style.fontWeight = config.fontWeight;
        } else {
          messageEl.style.removeProperty("font-weight");
        }
        messageEl.style.letterSpacing = config.letterSpacing || "0px";
        this.fitCoverInitialBorder(messageEl);
        return;
      }
      const lineHeightFactor = (config.lineHeightPercent || 100) / 100;
      const safeLineHeightFactor = lineHeightFactor > 0 ? lineHeightFactor : 1;
      const baseLineHeightFactor = (config.baseLineHeightPercent || config.lineHeightPercent || 100) / 100;
      const safeBaseLineHeightFactor = baseLineHeightFactor > 0 ? baseLineHeightFactor : 1;
      const baseLineHeight = rect.height / (config.baseMaxLines || config.maxLines);
      const lineHeight = rect.height / config.maxLines;
      const baseFontSize = baseLineHeight * 0.78 * safeBaseLineHeightFactor;
      const fontSize = config.isSmall ? baseFontSize * 0.7 : lineHeight * 0.78 * safeLineHeightFactor;
      frameEl.style.removeProperty("--cover-char-width");
      frameEl.style.setProperty("--cover-font-size", `${fontSize}px`);
      frameEl.style.setProperty("--cover-line-height", `${lineHeight}px`);
      messageEl.style.fontSize = `${fontSize}px`;
      messageEl.style.lineHeight = "normal";
      if (config.fontFamily) {
        messageEl.style.fontFamily = resolveFontFamily(config.fontFamily);
      } else {
        messageEl.style.removeProperty("font-family");
      }
      if (config.fontWeight) {
        messageEl.style.fontWeight = config.fontWeight;
      } else {
        messageEl.style.removeProperty("font-weight");
      }
      messageEl.style.letterSpacing = "0px";
      const fontSpec = `${config.fontWeight || "normal"} ${fontSize}px ${toCssFontFamily(resolveFontFamily(config.fontFamily))}`;
      if (document.fonts) {
        try {
          if (!document.fonts.check(fontSpec, "ABCDEFGHIJKLMNOPQRSTUVWXYZ")) {
            document.fonts.ready.then(() => {
              if (frameEl.isConnected) this.syncTypographyFor(frameEl, messageEl, dateEl);
            });
          }
        } catch (error) {
          console.warn("Cover customization: font check invalide", error);
        }
      }
      if (dateEl) {
        dateEl.style.fontSize = `${fontSize}px`;
        dateEl.style.lineHeight = "normal";
        if (config.fontFamily) {
          dateEl.style.fontFamily = resolveFontFamily(config.fontFamily);
        } else {
          dateEl.style.removeProperty("font-family");
        }
        if (config.fontWeight) {
          dateEl.style.fontWeight = config.fontWeight;
        } else {
          dateEl.style.removeProperty("font-weight");
        }
        dateEl.style.letterSpacing = "0px";
      }
      this.fitStretchedLines(messageEl);
    }

    applyFontColor() {
      if (!this.messageEl) return;
      const textColor = this.getTextColor();
      const patternColor = this.getPatternColor();
      if (this.isDebugEnabled()) {
        console.log("Cover customization text color", {
          textColor: ((this.configData || {}).config || {}).textColor,
          firstColor: ((this.configData || {}).config || {}).firstColor,
          appliedTextColor: textColor,
          appliedPatternColor: patternColor,
          debugColorPattern: ((this.configData || {}).config || {}).debugColorPattern
        });
      }
      this.applyFontColorTo(this.messageEl, this.dateEl, textColor);
      this.applyFontColorTo(this._lightboxMessageEl, this._lightboxDateEl, textColor);
      this.applyPatternColorTo(this.patternEl, patternColor);
      this.applyPatternColorTo(this._lightboxPatternEl, patternColor);
    }

    getTextColor() {
      const config = ((this.configData || {}).config || {});
      return (this.isCoverInitialLayout() && config.firstColor) || config.textColor || "#FEFEFE";
    }

    getPatternColor() {
      return ((this.configData || {}).config || {}).textColor || "#FEFEFE";
    }

    applyFontColorTo(messageEl, dateEl, color) {
      if (!messageEl || !color) return;
      messageEl.style.color = color;
      if (dateEl) dateEl.style.color = color;
    }

    applyPatternColorTo(patternEl, color) {
      if (!patternEl || !color) return;
      patternEl.style.backgroundColor = color;
    }

    applyPatternWidthTo(patternEl) {
      if (!patternEl) return;
      const config = (this.configData || {}).config || {};
      const patternWidth = toCssSize(
        this.sizeConfig && this.sizeConfig.patternWidth !== null && this.sizeConfig.patternWidth !== void 0 && this.sizeConfig.patternWidth !== ""
          ? this.sizeConfig.patternWidth
          : config.patternWidth
      );
      if (patternWidth) {
        patternEl.style.setProperty("--cover-pattern-width", patternWidth);
      } else {
        patternEl.style.removeProperty("--cover-pattern-width");
      }
    }

    getSelectedPattern() {
      const variantPatternValue = this.getPatternOptionValue(this.variant);
      return this.findPatternForValue(variantPatternValue);
    }

    findPatternForValue(value) {
      const normalized = normalizeValue(value);
      if (!normalized) return null;
      if (this.isCoverInitialLayout()) {
        // The shared size token (for example the first "S" in Plaid_initiale_S_-_A) is not the selected initial.
        const valueTokens = tokenizeValue(normalized);
        const initialKey = valueTokens[valueTokens.length - 1] || normalized;
        return this.patterns.find((pattern) => pattern.trailingToken === initialKey) || null;
      }
      const exactMatch = this.patterns.find((pattern) => pattern.candidates.includes(normalized));
      return exactMatch || this.patterns.find((pattern) => pattern.candidates.some((candidate) => matchesLoose(normalized, candidate))) || null;
    }

    getPatternOptionValue(variant) {
      const root = this.closest(".product") || document;
      const picker = this.formId ? root.querySelector(`variant-picker[form-id="${this.formId}"]`) : null;
      if (picker) {
        const fieldsets = Array.from(picker.querySelectorAll(".variant-picker__option"));
        const patternFieldset = fieldsets.find((fieldset) => {
          const legend = fieldset.querySelector("legend");
          if (!legend) return false;
          const text = normalizeValue(legend.textContent);
          const isInitialFieldset = this.isCoverInitialLayout() && (text.includes("initiale") || text.includes("initial"));
          return isInitialFieldset || text.includes("motif") || text.includes("pattern") || text.includes("imprime") || text.includes("imprimé");
        });
        if (patternFieldset) {
          const checkedInput = patternFieldset.querySelector("input[data-option-position]:checked");
          if (checkedInput) {
            const label = patternFieldset.querySelector(`label[for="${checkedInput.id}"]`);
            if (label) return label.textContent.trim();
          }
          const sampleInput = patternFieldset.querySelector("[data-option-position]");
          const position = sampleInput ? Number(sampleInput.getAttribute("data-option-position")) : null;
          if (position && variant && variant[`option${position}`]) return variant[`option${position}`];
        }
      }
      if (!variant) return null;
      if (!this.patterns.length) return null;
      return [variant.option1, variant.option2, variant.option3].find((optionValue) => this.findPatternForValue(optionValue)) || null;
    }

    renderPattern() {
      const pattern = this.getSelectedPattern();
      this.renderPatternTo(this.patternEl, pattern);
      this.renderPatternTo(this._lightboxPatternEl, pattern);
    }

    renderPatternTo(patternEl, pattern) {
      if (!patternEl) return;
      if (!pattern) {
        patternEl.hidden = true;
        patternEl.classList.remove("cover-customization__pattern--svg", "cover-customization__pattern--image");
        patternEl.style.removeProperty("background-image");
        patternEl.style.removeProperty("background-color");
        patternEl.style.removeProperty("mask-image");
        patternEl.style.removeProperty("-webkit-mask-image");
        patternEl.style.removeProperty("--cover-pattern-width");
        patternEl.removeAttribute("aria-label");
        patternEl.removeAttribute("role");
        return;
      }
      this.applyPatternWidthTo(patternEl);
      patternEl.classList.toggle("cover-customization__pattern--svg", pattern.isSvg);
      patternEl.classList.toggle("cover-customization__pattern--image", !pattern.isSvg);
      patternEl.setAttribute("role", "img");
      patternEl.setAttribute("aria-label", pattern.label || pattern.name || "Motif");
      if (pattern.isSvg) {
        patternEl.style.removeProperty("background-image");
        patternEl.style.maskImage = toCssUrl(pattern.url);
        patternEl.style.webkitMaskImage = toCssUrl(pattern.url);
        this.applyPatternColorTo(patternEl, this.getPatternColor());
      } else {
        patternEl.style.backgroundImage = toCssUrl(pattern.url);
        patternEl.style.removeProperty("background-color");
        patternEl.style.removeProperty("mask-image");
        patternEl.style.removeProperty("-webkit-mask-image");
      }
      patternEl.hidden = false;
    }

    renderMessage() {
      if (!this.messageInput) return;
      const messageValue = this.normalizeMessageForConfig(this.messageInput.value);
      if (this.messageInput.value !== messageValue) this.messageInput.value = messageValue;
      this.updateHeartControls();
      this.updateRemainingCount();
      if (!this.messageEl || !this.sizeConfig) return;
      const config = this.getEffectiveConfig();
      const rawMessage = messageValue.trim();
      this.messageEl.classList.toggle("cover-customization__message--single", !config.repeatOccurrences);

      if (!rawMessage) {
        this.messageEl.classList.remove("cover-customization__message--cover-initial");
        this.messageEl.innerHTML = "";
        if (this.totalMessageInput) this.totalMessageInput.value = "";
        if (this.dateEl) this.dateEl.hidden = true;
        this.setError(false);
        return;
      }

      if (!config.maxChars || !config.maxLines) {
        this.setError(false);
        return;
      }

      if (config.messageMinLength && rawMessage.length < config.messageMinLength) {
        this.setError(true);
        this.messageEl.innerHTML = "";
        if (this.totalMessageInput) this.totalMessageInput.value = "";
        return;
      }

      if (config.messageMaxLength && rawMessage.length > config.messageMaxLength) {
        this.setError(true);
        this.messageEl.innerHTML = "";
        if (this.totalMessageInput) this.totalMessageInput.value = "";
        return;
      }

      if (rawMessage.length > config.maxChars) {
        this.setError(true);
        this.messageEl.innerHTML = "";
        if (this.totalMessageInput) this.totalMessageInput.value = "";
        return;
      }

      if (config.isCoverInitial) {
        this.renderCoverInitialMessage(rawMessage);
        return;
      }

      this.setError(false);
      const word = `${rawMessage} `;
      let nbWord = config.repeatOccurrences ? Math.floor(config.maxChars / (rawMessage.length + 1)) : 1;
      let nbSpace = config.maxChars % (rawMessage.length + 1);
      if (nbSpace === rawMessage.length) {
        nbWord += 1;
        nbSpace = 0;
      } else {
        nbSpace += 1;
      }
      const textExclusionMask = parseTextExclusionMask(this.sizeConfig && this.sizeConfig.textExclusionMask);
      const renderMaskableText = (baseText) =>
        Array.from(baseText || "")
          .map((char) => `<span class="cover-customization__mask-char">${renderMessageFragment(char)}</span>`)
          .join("");
      const renderLine = (baseText, baseTransform = "", maskCharacters = false) => {
        const transformAttribute = baseTransform ? ` data-base-transform="${escapeHtml(baseTransform)}"` : "";
        const maskClass = maskCharacters ? " cover-customization__line-text--maskable" : "";
        const content = maskCharacters ? renderMaskableText(baseText) : renderMessageFragment(baseText);
        return `<span class="cover-customization__line-text${maskClass}" data-render-text="${escapeHtml(baseText)}"${transformAttribute}>${content}</span>`;
      };
      const dateEnabled = this.dateInputHidden && this.dateInputHidden.value === "Oui";
      const dateValue = this.dateInput ? normalizeMessageValue(this.dateInput.value).trim().slice(0, DATE_MAX_LENGTH) : "";
      if (this.dateInput && this.dateInput.value !== dateValue) this.dateInput.value = dateValue;
      const showDate = dateEnabled && dateValue.length > 0;
      const linesCount = Math.max(0, config.maxLines);
      const renderedLinesCount = config.repeatOccurrences ? linesCount : Math.min(linesCount, 1);
      const maskRepeatedText = config.repeatOccurrences && textExclusionMask;
      const linesHtml = [];
      for (let i = 0; i < renderedLinesCount; i += 1) {
        const isEvenLine = (i + 1) % 2 === 0;
        const isLastLine = i === renderedLinesCount - 1;
        const reservedDateChars = showDate && isLastLine ? dateValue.length + DATE_SPACING_RESERVE_CHARS : 0;
        const rotateSecondLine = config.repeatOccurrences && isEvenLine && config.secondLineMode === "rotate";
        const mirrorSecondLine = config.repeatOccurrences && isEvenLine && config.secondLineMode === "mirror";
        const plainSecondLine = config.repeatOccurrences && isEvenLine && config.secondLineMode === "plain";
        const offsetLine = config.infinite && mirrorSecondLine;
        const shortenSecondLine = plainSecondLine || rotateSecondLine || (!config.infinite && mirrorSecondLine);
        let lineWordCount = shortenSecondLine ? Math.max(1, nbWord - 1) : nbWord;
        if (offsetLine) lineWordCount += 1;
        if (config.repeatOccurrences && reservedDateChars) {
          while (lineWordCount > 1 && word.repeat(lineWordCount).slice(0, -1).length + reservedDateChars > config.maxChars) {
            lineWordCount -= 1;
          }
        }
        const lineText = config.repeatOccurrences ? word.repeat(lineWordCount).slice(0, -1) : rawMessage;
        const lineLength = Array.from(lineText).length;
        const trailingCount = Math.max(0, config.maxChars - lineLength);
        if (showDate && isLastLine) {
          const totalLineText = `${lineText}${" ".repeat(trailingCount)} ${dateValue}`;
          linesHtml.push(
            `<span class="cover-customization__line cover-customization__line--with-date" data-date-line="true" data-repeat-message="${escapeHtml(rawMessage)}" data-repeat-word-count="${lineWordCount}" data-line-max-chars="${config.maxChars}" data-date-value="${escapeHtml(dateValue)}" data-total-line-text="${escapeHtml(totalLineText)}">${renderLine(lineText, "", maskRepeatedText)}<span class="cover-customization__line-date">${renderMessageFragment(dateValue)}</span></span>`
          );
        } else {
          const lineClasses = ["cover-customization__line"];
          if (offsetLine) lineClasses.push("cover-customization__line--offset");
          if (rotateSecondLine) lineClasses.push("cover-customization__line--second-rotate");
          if (mirrorSecondLine) lineClasses.push("cover-customization__line--second-mirror");
          const lineClass = lineClasses.join(" ");
          const totalLineText = `${lineText}${" ".repeat(trailingCount)}`;
          linesHtml.push(`<span class="${lineClass}" data-offset-chars="${Math.ceil((rawMessage.length + 1) / 2)}" data-total-line-text="${escapeHtml(totalLineText)}">${renderLine(lineText, "", maskRepeatedText)}</span>`);
        }
      }
      this.messageEl.innerHTML = linesHtml.join("");
      this.fitStretchedLines(this.messageEl);
      this.applyTextExclusionMaskToMessage(this.messageEl);
      this.syncTotalMessageInputFromRenderedLines();
      if (this.dateEl) {
        this.dateEl.hidden = true;
        this.dateEl.textContent = "";
      }
      this.syncLightboxMessage();
    }

    renderCoverInitialMessage(rawMessage) {
      if (!this.messageEl) return;
      const config = this.getEffectiveConfig();
      const word = `${rawMessage} `;
      const repeatCount = config.repeatOccurrences ? Math.max(1, Math.floor((config.maxChars + 1) / word.length)) : 1;
      const borderMessage = config.repeatOccurrences ? word.repeat(repeatCount).slice(0, -1) : rawMessage;
      const renderedMessage = config.repeatOccurrences
        ? Array.from({ length: repeatCount }, (_, index) => {
            const mirrorClass = (index + 1) % 2 === 0 ? " cover-customization__border-occurrence--mirror" : "";
            return `<span class="cover-customization__border-occurrence${mirrorClass}">${renderMessageFragment(rawMessage)}</span>`;
          }).join(" ")
        : renderMessageFragment(rawMessage);
      const escapedMessage = escapeHtml(borderMessage);
      const sides = ["top", "right", "bottom", "left"];
      this.messageEl.classList.remove("cover-customization__message--single");
      this.messageEl.classList.add("cover-customization__message--cover-initial");
      this.messageEl.innerHTML = sides
        .map(
          (side) =>
            `<span class="cover-customization__border-line cover-customization__border-line--${side}" data-total-line-text="${escapedMessage}"><span class="cover-customization__border-text" data-render-text="${escapedMessage}">${renderedMessage}</span></span>`
        )
        .join("");
      if (this.totalMessageInput) this.totalMessageInput.value = rawMessage;
      if (this.dateEl) {
        this.dateEl.hidden = true;
        this.dateEl.textContent = "";
      }
      this.syncTypography();
      this.applyFontColor();
      this.syncLightboxMessage();
    }

    applyTextExclusionMaskToMessage(messageEl) {
      if (!messageEl) return;
      const chars = Array.from(messageEl.querySelectorAll(".cover-customization__mask-char"));
      chars.forEach((charEl) => {
        charEl.style.visibility = "";
      });
      if (!chars.length) return;
      const mask = parseTextExclusionMask(this.sizeConfig && this.sizeConfig.textExclusionMask);
      const frameEl = messageEl.closest(".cover-customization__frame");
      if (!mask || !frameEl) return;
      const frameRect = frameEl.getBoundingClientRect();
      if (!frameRect.width || !frameRect.height) return;
      chars.forEach((charEl) => {
        if (isBlankCharacter(charEl.textContent || "")) return;
        const rect = charEl.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = ((rect.left + rect.width / 2 - frameRect.left) / frameRect.width) * 100;
        const y = ((rect.top + rect.height / 2 - frameRect.top) / frameRect.height) * 100;
        if (pointIsInsideTextExclusionMask(mask, x, y)) {
          charEl.style.visibility = "hidden";
        }
      });
    }

    fitStretchedLines(messageEl) {
      if (!messageEl) return;
      if (messageEl.classList.contains("cover-customization__message--cover-initial")) {
        this.fitCoverInitialBorder(messageEl);
        return;
      }
      if (!this.getEffectiveConfig().repeatOccurrences) {
        messageEl.querySelectorAll(".cover-customization__line-text, .cover-customization__line-date").forEach((textEl) => {
          textEl.style.letterSpacing = "0px";
          textEl.style.transform = "";
        });
        return;
      }
      const config = this.getEffectiveConfig();
      let previousOddLineSpacing = "0px";
      let previousOddLineTransform = "";
      messageEl.querySelectorAll(".cover-customization__line").forEach((lineEl, index) => {
        const textEl = lineEl.querySelector(".cover-customization__line-text");
        const dateEl = lineEl.querySelector(".cover-customization__line-date");
        if (!textEl) return;
        textEl.style.letterSpacing = "0px";
        textEl.style.transform = "";
        if (dateEl) {
          dateEl.style.letterSpacing = "0px";
          dateEl.style.transform = "";
        }
        if (lineEl.classList.contains("cover-customization__line--with-date")) {
          textEl.style.letterSpacing = previousOddLineSpacing;
          textEl.style.transform = previousOddLineTransform;
          if (dateEl) dateEl.style.letterSpacing = previousOddLineSpacing;
          this.fitDateLine(lineEl, textEl, dateEl, previousOddLineSpacing, previousOddLineTransform);
          return;
        }
        if (index % 2 === 1) {
          textEl.style.letterSpacing = previousOddLineSpacing;
          textEl.style.transform = previousOddLineTransform;
          if (config.infinite && lineEl.classList.contains("cover-customization__line--offset")) {
            const offsetChars = toNumber(lineEl.getAttribute("data-offset-chars")) || 0;
            const lineWidth = lineEl.getBoundingClientRect().width;
            const textLength = Array.from(textEl.getAttribute("data-render-text") || textEl.textContent || "").length;
            const offset = lineWidth && textLength ? (lineWidth / textLength) * offsetChars : 0;
            textEl.style.transform = `${previousOddLineTransform ? `${previousOddLineTransform} ` : ""}translateX(-${offset}px)`;
          }
          return;
        }
        const lineWidth = lineEl.getBoundingClientRect().width;
        const textWidth = textEl.getBoundingClientRect().width;
        const textLength = Array.from(textEl.getAttribute("data-render-text") || textEl.textContent || "").length;
        if (!lineWidth || !textWidth || !textLength) return;
        if (textLength > 1 && textWidth < lineWidth) {
          const spacing = (lineWidth - textWidth) / (textLength - 1);
          textEl.style.letterSpacing = `${spacing}px`;
          previousOddLineSpacing = `${spacing}px`;
          previousOddLineTransform = "";
        } else if (textLength === 1 || textWidth > lineWidth) {
          const scale = lineWidth / textWidth;
          textEl.style.transform = `scaleX(${scale})`;
          previousOddLineSpacing = "0px";
          previousOddLineTransform = `scaleX(${scale})`;
        } else {
          previousOddLineSpacing = "0px";
          previousOddLineTransform = "";
        }
      });
    }

    fitCoverInitialBorder(messageEl) {
      if (!messageEl) return;
      const repeatOccurrences = this.getEffectiveConfig().repeatOccurrences;
      messageEl.querySelectorAll(".cover-customization__border-line").forEach((lineEl) => {
        const textEl = lineEl.querySelector(".cover-customization__border-text");
        if (!textEl) return;
        textEl.style.letterSpacing = "0px";
        textEl.style.transform = "";
        textEl.style.transformOrigin = "left center";
        textEl.style.maxWidth = repeatOccurrences ? "none" : "100%";
        textEl.style.overflow = repeatOccurrences ? "visible" : "hidden";
        if (!repeatOccurrences) return;

        const lineWidth = lineEl.clientWidth;
        const textWidth = textEl.scrollWidth;
        const textLength = Array.from(textEl.getAttribute("data-render-text") || textEl.textContent || "").length;
        if (!lineWidth || !textWidth || !textLength) return;
        if (textLength > 1 && textWidth < lineWidth) {
          textEl.style.letterSpacing = `${(lineWidth - textWidth) / textLength}px`;
        } else if (textWidth > lineWidth) {
          textEl.style.transform = `scaleX(${lineWidth / textWidth})`;
        }
      });
    }

    fitDateLine(lineEl, textEl, dateEl, letterSpacing, transform) {
      if (!lineEl || !textEl || !dateEl || lineEl.getAttribute("data-date-line") !== "true") return;

      const repeatMessage = lineEl.getAttribute("data-repeat-message") || "";
      let repeatCount = Number.parseInt(lineEl.getAttribute("data-repeat-word-count") || "0", 10);
      if (!repeatMessage || !Number.isFinite(repeatCount) || repeatCount <= 1) return;

      const applyDateLineStyle = () => {
        textEl.style.letterSpacing = letterSpacing;
        textEl.style.transform = transform;
        dateEl.style.letterSpacing = letterSpacing;
        dateEl.style.transform = "";
      };
      const lineFits = () => {
        const lineRect = lineEl.getBoundingClientRect();
        const textRect = textEl.getBoundingClientRect();
        const dateRect = dateEl.getBoundingClientRect();
        const style = window.getComputedStyle ? window.getComputedStyle(lineEl) : null;
        const parsedColumnGap = style ? Number.parseFloat(style.columnGap || "") : Number.NaN;
        const parsedGap = style ? Number.parseFloat(style.gap || "") : Number.NaN;
        const gap = Number.isFinite(parsedColumnGap) ? parsedColumnGap : Number.isFinite(parsedGap) ? parsedGap : 0;
        const paddingLeft = style ? Number.parseFloat(style.paddingLeft || "0") || 0 : 0;
        const paddingRight = style ? Number.parseFloat(style.paddingRight || "0") || 0 : 0;
        const availableWidth = Math.max(0, lineRect.width - paddingLeft - paddingRight);
        if (!lineRect.width || !dateRect.width) return true;
        return textRect.width + dateRect.width + gap <= availableWidth + 0.5 && dateRect.right <= lineRect.right - paddingRight + 0.5;
      };
      const updateLineText = () => {
        const lineText = `${repeatMessage} `.repeat(repeatCount).slice(0, -1);
        const maxChars = toNumber(lineEl.getAttribute("data-line-max-chars"));
        const dateValue = lineEl.getAttribute("data-date-value") || "";
        const trailingCount = maxChars ? Math.max(0, maxChars - Array.from(lineText).length) : 0;
        textEl.setAttribute("data-render-text", lineText);
        textEl.innerHTML = renderMessageFragment(lineText);
        lineEl.setAttribute("data-repeat-word-count", `${repeatCount}`);
        lineEl.setAttribute("data-total-line-text", `${lineText}${" ".repeat(trailingCount)} ${dateValue}`);
        applyDateLineStyle();
      };

      while (!lineFits() && repeatCount > 1) {
        repeatCount -= 1;
        updateLineText();
      }
    }

    syncTotalMessageInputFromRenderedLines() {
      if (!this.totalMessageInput || !this.messageEl) return;
      this.totalMessageInput.value = Array.from(this.messageEl.querySelectorAll(".cover-customization__line"))
        .map((lineEl) => lineEl.getAttribute("data-total-line-text") || "")
        .join("\n");
    }

    truncateMessageForCurrentSize() {
      if (!this.messageInput) return;
      const config = this.getEffectiveConfig();
      const maxLength = config.messageMaxLength;
      const currentValue = this.normalizeMessageForConfig(this.messageInput.value);
      if (this.messageInput.value !== currentValue) this.messageInput.value = currentValue;
      if (!maxLength || currentValue.length <= maxLength) return;
      this.messageInput.value = currentValue.slice(0, maxLength);
      this.setModifiedNotice(true);
    }

    setModifiedNotice(isVisible) {
      if (!this.modifiedNoticeEl) return;
      this.modifiedNoticeEl.hidden = !isVisible;
    }

    setError(isError) {
      if (this.errorEl) this.errorEl.hidden = !isError;
      if (this.messageInput) this.messageInput.setCustomValidity(isError ? this.errorMessage : "");
    }

    attachOverlay() {
      const media = this.findTargetMedia();
      if (!media) {
      this.stopWaitingForImageLoad();
      if (this.overlayEl && this.overlayEl.parentElement) {
        this.overlayEl.parentElement.removeChild(this.overlayEl);
      }
      if (this._targetMedia) {
        this._targetMedia.classList.remove("has-cover-customization-overlay");
      }
      this._targetMedia = null;
      return;
     }
      this.ensureMediaVisible(media);
      if (this._targetMedia === media && this.overlayEl) {
        this.syncOverlayWithImageLoad(media);
        return;
      }
      this.stopWaitingForImageLoad();
      if (this.overlayEl && this.overlayEl.parentElement) {
        this.overlayEl.parentElement.removeChild(this.overlayEl);
      }
      if (this._targetMedia) {
        this._targetMedia.classList.remove("has-cover-customization-overlay");
      }
      this._targetMedia = media;
      this._targetMedia.classList.add("has-cover-customization-overlay");
      this.overlayEl = document.createElement("div");
      this.overlayEl.className = "cover-customization__overlay";
      this.metaEl = document.createElement("div");
      this.metaEl.className = "cover-customization__meta";
      this.metaEl.hidden = true;
      if (this.isDebugEnabled() && this.metaInfoText) {
        this.metaEl.textContent = this.metaInfoText;
        this.metaEl.hidden = false;
      }
      this.patternEl = document.createElement("div");
      this.patternEl.className = "cover-customization__pattern";
      this.patternEl.hidden = true;
      this.frameEl = document.createElement("div");
      this.frameEl.className = "cover-customization__frame";
      this.messageEl = document.createElement("div");
      this.messageEl.className = "cover-customization__message";
      this.dateEl = document.createElement("div");
      this.dateEl.className = "cover-customization__date";
      this.dateEl.hidden = true;
      this.frameEl.append(this.messageEl, this.dateEl);
      this.overlayEl.append(this.metaEl, this.patternEl, this.frameEl);
      this._targetMedia.append(this.overlayEl);
      this.observeResize();
      this.syncOverlayWithImageLoad(media);
    }

    stopWaitingForImageLoad() {
      if (!this._imageLoadAbortController) return;
      this._imageLoadAbortController.abort();
      this._imageLoadAbortController = null;
    }

    markVariantMediaPending() {
      this._isVariantMediaPending = true;
      this.stopWaitingForImageLoad();
      if (this.overlayEl) this.overlayEl.hidden = true;
    }

    clearVariantMediaPending() {
      this._isVariantMediaPending = false;
    }

    syncOverlayWithImageLoad(media) {
      if (!this.overlayEl) return;
      const image = media ? media.querySelector("img") : null;
      this.stopWaitingForImageLoad();
      if (this._isVariantMediaPending) {
        this.overlayEl.hidden = true;
        return;
      }
      if (!image || image.complete) {
        this.overlayEl.hidden = false;
        return;
      }

      this.overlayEl.hidden = true;
      const controller = new AbortController();
      this._imageLoadAbortController = controller;
      const revealOverlay = () => {
        if (controller.signal.aborted) return;
        controller.abort();
        if (this._imageLoadAbortController === controller) {
          this._imageLoadAbortController = null;
        }
        requestAnimationFrame(() => {
          if (this._targetMedia !== media || !this.overlayEl) return;
          this.overlayEl.hidden = false;
          this.applyFrame();
          this.syncTypography();
          this.applyFontColor();
          this.renderPattern();
          this.renderMessage();
          this.updateLightboxOverlay();
        });
      };

      image.addEventListener("load", revealOverlay, { once: true, signal: controller.signal });
      image.addEventListener("error", revealOverlay, { once: true, signal: controller.signal });
      if (typeof image.decode === "function") {
        image.decode().then(revealOverlay).catch(() => {});
      }
    }

    ensureMediaVisible(media) {
      if (!media) return;
      const root = this.closest(".product") || document;
      const gallery = root.querySelector("product-gallery");
      const carousel = gallery ? gallery.querySelector(".product-gallery__carousel") : null;
      const allMedias = carousel ? Array.from(carousel.querySelectorAll(".product-gallery__media")) : Array.from(root.querySelectorAll(".product-gallery__media"));
      const mediaIndex = allMedias.indexOf(media);
      if (mediaIndex < 0) {
        media.hidden = false;
        return;
      }

      if (carousel && typeof carousel.filter === "function") {
        const nextFilteredIndexes = allMedias
          .map((item, index) => (item.hasAttribute("hidden") && index !== mediaIndex ? index : null))
          .filter((index) => index !== null);
        carousel.filter(nextFilteredIndexes);
        if (gallery) gallery.setAttribute("filtered-indexes", JSON.stringify(nextFilteredIndexes.map((index) => index + 1)));
        if (typeof carousel.select === "function") {
          const visibleMediaIndex = allMedias.filter((item) => !item.hasAttribute("hidden")).indexOf(media);
          if (visibleMediaIndex >= 0) carousel.select(visibleMediaIndex, { instant: true });
        }
      } else {
        media.hidden = false;
        media.scrollIntoView({ block: "start", behavior: "smooth" });
      }

      const mediaId = media.getAttribute("data-media-id");
      if (mediaId) {
        root.querySelectorAll(`.product-gallery__thumbnail[data-media-id="${mediaId}"]`).forEach((thumbnail) => {
          thumbnail.hidden = false;
        });
      }

      root.querySelectorAll("carousel-navigation[aria-controls] > button").forEach((button, index) => {
        if (index === mediaIndex) button.hidden = false;
      });
    }

    observeResize() {
      if (!this.frameEl) return;
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._resizeObserver = new ResizeObserver(() => {
        this.syncTypography();
        this.renderMessage();
      });
      this._resizeObserver.observe(this.frameEl);
    }

    observeLightbox() {
      if (this._lightboxObserver) return;
      this._lightboxObserver = new MutationObserver(() => this.handleLightboxStateChange());
      this._lightboxObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "aria-hidden"]
      });
      this.handleLightboxStateChange();
    }

    handleLightboxStateChange() {
      const pswp = document.querySelector(".pswp");
      if (pswp && this.isLightboxOpen(pswp)) {
        this.startLightboxTracking();
      } else {
        this.stopLightboxTracking();
      }
    }

    isLightboxOpen(pswp) {
      if (!pswp) return false;
      if (pswp.classList.contains("pswp--open")) return true;
      const ariaHidden = pswp.getAttribute("aria-hidden");
      if (ariaHidden === "false") return true;
      if (pswp.style.display && pswp.style.display !== "none") return true;
      return false;
    }

    startLightboxTracking() {
      if (this._lightboxRaf) return;
      const tick = () => {
        this._lightboxRaf = null;
        this.updateLightboxOverlay();
        const pswp = document.querySelector(".pswp");
        if (pswp && this.isLightboxOpen(pswp)) {
          this._lightboxRaf = requestAnimationFrame(tick);
        } else {
          this.stopLightboxTracking();
        }
      };
      this._lightboxRaf = requestAnimationFrame(tick);
    }

    stopLightboxTracking() {
      if (this._lightboxRaf) {
        cancelAnimationFrame(this._lightboxRaf);
        this._lightboxRaf = null;
      }
      this.teardownLightboxOverlay();
    }

    teardownLightboxOverlay() {
      if (this._lightboxOverlay && this._lightboxOverlay.parentElement) {
        this._lightboxOverlay.parentElement.removeChild(this._lightboxOverlay);
      }
      this._lightboxOverlay = null;
      this._lightboxPatternEl = null;
      this._lightboxFrame = null;
      this._lightboxMessageEl = null;
      this._lightboxDateEl = null;
      this._lightboxSlide = null;
    }

    updateLightboxOverlay() {
      const pswp = document.querySelector(".pswp");
      if (!pswp || !this.isLightboxOpen(pswp) || !this.sizeConfig) {
        this.teardownLightboxOverlay();
        return;
      }
      const slide =
        pswp.querySelector(".pswp__item[aria-hidden=\"false\"]") ||
        pswp.querySelector(".pswp__item--current") ||
        pswp.querySelector(".pswp__item");
      if (!slide) {
        this.teardownLightboxOverlay();
        return;
      }
      const img = slide.querySelector("img.pswp__img");
      if (!img) {
        this.teardownLightboxOverlay();
        return;
      }
      if (!this.matchesImageAlt(img)) {
        this.teardownLightboxOverlay();
        return;
      }
      this.ensureLightboxOverlay(slide);
      const slideRect = slide.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      if (!imgRect.width || !imgRect.height || !slideRect.width || !slideRect.height) return;
      const left = imgRect.left - slideRect.left;
      const top = imgRect.top - slideRect.top;
      this._lightboxOverlay.style.left = `${left}px`;
      this._lightboxOverlay.style.top = `${top}px`;
      this._lightboxOverlay.style.width = `${imgRect.width}px`;
      this._lightboxOverlay.style.height = `${imgRect.height}px`;
      this.applyFrameToElement(this._lightboxFrame);
      this.syncTypographyFor(this._lightboxFrame, this._lightboxMessageEl, this._lightboxDateEl);
      this.applyFontColor();
      this.renderPattern();
      this.syncLightboxMessage();
    }

    ensureLightboxOverlay(slide) {
      if (this._lightboxOverlay && this._lightboxSlide === slide) return;
      this.teardownLightboxOverlay();
      this._lightboxSlide = slide;
      this._lightboxOverlay = document.createElement("div");
      this._lightboxOverlay.className = "cover-customization__overlay cover-customization__overlay--lightbox";
      this._lightboxOverlay.style.position = "absolute";
      this._lightboxOverlay.style.pointerEvents = "none";
      this._lightboxPatternEl = document.createElement("div");
      this._lightboxPatternEl.className = "cover-customization__pattern";
      this._lightboxPatternEl.hidden = true;
      this._lightboxFrame = document.createElement("div");
      this._lightboxFrame.className = "cover-customization__frame";
      this._lightboxMessageEl = document.createElement("div");
      this._lightboxMessageEl.className = "cover-customization__message";
      this._lightboxDateEl = document.createElement("div");
      this._lightboxDateEl.className = "cover-customization__date";
      this._lightboxDateEl.hidden = true;
      this._lightboxFrame.append(this._lightboxMessageEl, this._lightboxDateEl);
      this._lightboxOverlay.append(this._lightboxPatternEl, this._lightboxFrame);
      slide.append(this._lightboxOverlay);
    }

    matchesImageAlt(img) {
      const sizeAlt = this.sizeConfig && this.sizeConfig.imageAlt ? this.sizeConfig.imageAlt : "";
      if (!sizeAlt) return true;
      const altCandidates = sizeAlt
        .split("|")
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map(normalizeValue);
      const altValue = normalizeValue(img.getAttribute("alt"));
      if (!altCandidates.some((candidate) => matchesLoose(altValue, candidate))) return false;
      const variantPatternValue = this.getPatternOptionValue(this.variant);
      const selectedPattern = this.getSelectedPattern();
      const patternCandidates = [
        ...(selectedPattern ? selectedPattern.candidates : []),
        normalizeValue(variantPatternValue)
      ].filter(Boolean);
      const imageHasKnownPattern = this.patterns.some((pattern) =>
        pattern.candidates.some((candidate) => matchesLoose(altValue, candidate))
      );
      if (!imageHasKnownPattern) return true;
      return patternCandidates.some((candidate) => matchesLoose(altValue, candidate));
    }

    syncLightboxMessage() {
      if (!this._lightboxMessageEl || !this.messageEl) return;
      this._lightboxMessageEl.innerHTML = this.messageEl.innerHTML;
      this._lightboxMessageEl.classList.toggle(
        "cover-customization__message--single",
        this.messageEl.classList.contains("cover-customization__message--single")
      );
      this._lightboxMessageEl.classList.toggle(
        "cover-customization__message--cover-initial",
        this.messageEl.classList.contains("cover-customization__message--cover-initial")
      );
      this.fitStretchedLines(this._lightboxMessageEl);
      this.applyTextExclusionMaskToMessage(this._lightboxMessageEl);
      if (this._lightboxDateEl && this.dateEl) {
        this._lightboxDateEl.textContent = this.dateEl.textContent;
        this._lightboxDateEl.hidden = this.dateEl.hidden;
      }
    }

    findTargetMedia() {
      const root = this.closest(".product") || document;
      const medias = Array.from(root.querySelectorAll(".product-gallery__media"));
      if (!medias.length) return null;
      const images = medias
        .map((media) => ({
          media,
          image: media.querySelector("img[alt]")
        }))
        .filter((item) => item.image);
      if (!images.length) return null;
      const sizeAlt = this.sizeConfig && this.sizeConfig.imageAlt ? this.sizeConfig.imageAlt : "";
      if (sizeAlt) {
        const altCandidates = sizeAlt
          .split("|")
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map(normalizeValue);
        const variantPatternValue = this.getPatternOptionValue(this.variant);
        const selectedPattern = this.getSelectedPattern();
        const patternCandidates = [
          ...(selectedPattern ? selectedPattern.candidates : []),
          normalizeValue(variantPatternValue)
        ].filter(Boolean);
        const matchesSize = (item) => {
          const altValue = normalizeValue(item.image.getAttribute("alt"));
          return altCandidates.some((candidate) => matchesLoose(altValue, candidate));
        };
        const matchesSelectedPattern = (item) => {
          const altValue = normalizeValue(item.image.getAttribute("alt"));
          return patternCandidates.some((candidate) => matchesLoose(altValue, candidate));
        };
        const findMatch = (list) =>
          list.find((item) => matchesSize(item));
        const findPatternMatch = (list) =>
          patternCandidates.length ? list.find((item) => matchesSize(item) && matchesSelectedPattern(item)) : null;
        const visibleList = images.filter((item) => !item.media.hasAttribute("hidden"));
        const visiblePatternMatch = findPatternMatch(visibleList);
        if (visiblePatternMatch) return visiblePatternMatch.media;
        const patternMatch = findPatternMatch(images);
        if (patternMatch) return patternMatch.media;
        const visibleMatch = findMatch(visibleList);
        if (visibleMatch) return visibleMatch.media;
        const match = findMatch(images);
        if (match) return match.media;
        return null;
      }
      const visibleFallback = images.find((item) => !item.media.hasAttribute("hidden"));
      return (visibleFallback || images[0]).media;
    }

    scrollToImage() {
      if (!this._targetMedia) return;
      this._targetMedia.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    scrollToImageIfMessageFilled() {
      if (!this.messageInput || !this.messageInput.value.trim()) return;
      this.scrollToImage();
    }
  }

  if (!window.customElements.get("cover-customization")) {
    window.customElements.define("cover-customization", CoverCustomization);
  }
})();
